/** Configuration — Publishing — display areas, sample source results, actions. Full Source Result / Condition catalogues remain NOT CONFIRMED. */

export const PUBLISHING_ROUTE = '/admin/configuration/publishing';

export const PUB_MODULE_OPTIONS = [
  { value: 'business_profiles', label: 'Business Profiles' },
  { value: 'posts', label: 'Posts' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'compliance', label: 'Compliance' },
];

/** Ten supported Display Areas and stated boundaries (section 3). Module/item mappings within registered Source Results remain NOT CONFIRMED. */
export const PUB_DISPLAY_AREA_OPTIONS = [
  {
    value: 'whole_business_profile',
    label: 'Whole Business Profile',
    module: 'business_profiles',
    target: 'whole_profile',
    boundary: 'Complete Business Profile.',
    audienceWhenVisible: 'public',
  },
  {
    value: 'business_profile_core_content',
    label: 'Business Profile Core Content',
    module: 'business_profiles',
    target: 'profile_item',
    boundary: 'One supported individual core-content item.',
    audienceWhenVisible: 'public',
  },
  {
    value: 'structured_contact_details',
    label: 'Structured Contact Details',
    module: 'business_profiles',
    target: 'profile_item',
    boundary: 'Supported structured-contact target.',
    audienceWhenVisible: 'active_verified_customers_only',
  },
  {
    value: 'allowed_external_links',
    label: 'Allowed External Links',
    module: 'business_profiles',
    target: 'profile_item',
    boundary: 'Supported allowed-link target.',
    audienceWhenVisible: 'active_verified_customers_only',
  },
  {
    value: 'post',
    label: 'Post',
    module: 'posts',
    target: 'post',
    boundary: 'Whole Post, including its supported title, description, images and attachments.',
    audienceWhenVisible: 'public',
  },
  {
    value: 'review',
    label: 'Review',
    module: 'reviews',
    target: 'review',
    boundary: 'One Customer Review.',
    audienceWhenVisible: 'public',
  },
  {
    value: 'business_reply',
    label: 'Business Reply',
    module: 'reviews',
    target: 'business_reply',
    boundary: 'One Business Reply linked to its Review.',
    audienceWhenVisible: 'public',
  },
  {
    value: 'licence_display',
    label: 'Licence Display',
    module: 'compliance',
    target: 'credential',
    boundary: 'Approved Licence display information. Excludes private evidence, verification documents, reviewer notes and decision evidence.',
    audienceWhenVisible: 'public',
  },
  {
    value: 'badge_display',
    label: 'Badge Display',
    module: 'compliance',
    target: 'credential',
    boundary: 'Approved Badge display information. Excludes private evidence, verification documents, reviewer notes and decision evidence.',
    audienceWhenVisible: 'public',
  },
  {
    value: 'trade_ticket_display',
    label: 'Trade Ticket Display',
    module: 'compliance',
    target: 'credential',
    boundary: 'Approved Trade Ticket display information. Excludes private evidence, verification documents, reviewer notes and decision evidence.',
    audienceWhenVisible: 'public',
  },
];

export const PUBLISHING_ACTION_OPTIONS = [
  { value: 'publish', label: 'Publish', displayState: 'visible' },
  { value: 'republish', label: 'Republish', displayState: 'visible' },
  { value: 'hide', label: 'Hide', displayState: 'hidden' },
  { value: 'restore', label: 'Restore', displayState: 'visible' },
];

export const DISPLAY_STATE_OPTIONS = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
];

export const VISIBILITY_AUDIENCE_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'active_verified_customers_only', label: 'Active Verified Customers Only' },
  { value: 'not_applicable', label: 'Not Applicable' },
];

/**
 * Sample Source Results for Table 1 / section 6 hide requirements only.
 * Complete registered catalogue, permanent references, owners, required context and mappings remain NOT CONFIRMED.
 */
export const SOURCE_RESULT_OPTIONS = [
  {
    value: 'approved_for_publication',
    label: 'Approved for Publication',
    reference: 'sample-src-approved-publication',
    ownerKey: 'source_module',
    owner: '',
    requiredContext: 'Authorised approval result for publication of the exact Display Area target. Full registered definition NOT CONFIRMED.',
    requiredContextKey: 'approved_publication',
    contextDefined: true,
    displayAreas: ['business_profile_core_content', 'post', 'review'],
    compatibleActions: ['publish', 'republish'],
    requiresHiding: false,
    itemOnly: true,
    available: true,
  },
  {
    value: 'approved_for_display',
    label: 'Approved for Display',
    reference: 'sample-src-approved-display',
    ownerKey: 'fixed',
    owner: 'Pricing Plan',
    requiredContext: 'Authorised entitlement result for restricted display. Full registered definition NOT CONFIRMED.',
    requiredContextKey: 'approved_entitlement',
    contextDefined: true,
    displayAreas: ['structured_contact_details', 'allowed_external_links'],
    compatibleActions: ['publish'],
    requiresHiding: false,
    itemOnly: true,
    available: true,
  },
  {
    value: 'user_flag',
    label: 'User Flag',
    reference: 'sample-src-user-flag',
    ownerKey: 'fixed',
    owner: 'Configuration — Flag',
    requiredContext: 'Qualified User Flag result for a Post. Full registered definition NOT CONFIRMED.',
    requiredContextKey: 'qualified_user_flag',
    contextDefined: true,
    displayAreas: ['post'],
    compatibleActions: ['hide'],
    requiresHiding: true,
    itemOnly: false,
    available: true,
  },
  {
    value: 'system_flag',
    label: 'System Flag',
    reference: 'sample-src-system-flag',
    ownerKey: 'fixed',
    owner: 'Configuration — Flag',
    requiredContext: 'Full registered definition NOT CONFIRMED.',
    requiredContextKey: 'qualified_system_flag',
    contextDefined: true,
    displayAreas: [
      'post',
      'review',
      'business_reply',
      'business_profile_core_content',
      'structured_contact_details',
      'allowed_external_links',
    ],
    compatibleActions: ['hide'],
    requiresHiding: true,
    itemOnly: true,
    available: true,
  },
  {
    value: 'approved_for_restoration',
    label: 'Approved for Restoration',
    reference: 'sample-src-approved-restoration',
    ownerKey: 'fixed',
    owner: 'Moderation',
    requiredContext: 'Authorised restoration decision for the exact Display Area target. Full registered definition NOT CONFIRMED.',
    requiredContextKey: 'approved_restoration',
    contextDefined: true,
    displayAreas: [
      'post',
      'review',
      'business_reply',
      'business_profile_core_content',
      'structured_contact_details',
      'allowed_external_links',
    ],
    compatibleActions: ['restore'],
    requiresHiding: false,
    itemOnly: false,
    available: true,
  },
];

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals', multi: false },
  { value: 'does_not_equal', label: 'Does Not Equal', multi: false },
  { value: 'is_any_of', label: 'Is Any Of', multi: true },
  { value: 'is_not_any_of', label: 'Is Not Any Of', multi: true },
];

/** Optional Condition catalogue — NOT CONFIRMED. Keep empty until confirmed. */
export const CONDITION_FIELD_OPTIONS = [];
export const CONDITION_VALUE_OPTIONS = {};

const SCOPE_REPEAT_FIELDS = new Set(['module', 'display_area', 'source_result']);

const LEGACY_MODULE_MAP = {
  business: 'business_profiles',
  businesses: 'business_profiles',
};

const LEGACY_DISPLAY_AREA_MAP = {
  business_profile: 'business_profile_core_content',
  profile_gallery: 'business_profile_core_content',
  contact_details: 'structured_contact_details',
  external_links: 'allowed_external_links',
  service_post: 'post',
  review_reply: 'business_reply',
};

const LEGACY_SOURCE_RESULT_MAP = {
  eligibility_result: 'approved_for_publication',
  eligible_for_public_display: 'approved_for_publication',
  plan_access_result: 'approved_for_display',
  content_control_outcome: 'system_flag',
  blocked_from_release: 'system_flag',
  hide_approved: 'system_flag',
  restore_approved: 'approved_for_restoration',
  publish_approved: 'approved_for_publication',
};

const LEGACY_ACTION_MAP = {
  live: 'publish',
  set_live: 'publish',
  restore_live: 'restore',
  hidden: 'hide',
  keep_hidden: 'hide',
  unpublished: 'hide',
  unpublish: 'hide',
};

const LEGACY_DISPLAY_STATE_MAP = {
  live: 'visible',
  protected_visible: 'visible',
  unpublished: 'hidden',
  removed_from_display: 'hidden',
};

const LEGACY_AUDIENCE_MAP = {
  visitor: 'public',
  active_verified_customers: 'active_verified_customers_only',
  no_audience: 'not_applicable',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export function operatorIsMulti(operator) {
  return CONDITION_OPERATOR_OPTIONS.find((option) => option.value === operator)?.multi === true;
}

export function withUnavailableLabel(label, available) {
  if (!label) return available === false ? 'Unavailable' : '';
  return available === false ? `${label} Unavailable` : label;
}

export function normalizePublishingModule(value) {
  if (!value) return '';
  if (PUB_MODULE_OPTIONS.some((option) => option.value === value)) return value;
  return LEGACY_MODULE_MAP[value] ?? value;
}

export function normalizeDisplayArea(value) {
  if (!value) return '';
  if (PUB_DISPLAY_AREA_OPTIONS.some((option) => option.value === value)) return value;
  return LEGACY_DISPLAY_AREA_MAP[value] || value;
}

export function normalizeSourceResult(value) {
  if (!value) return '';
  if (SOURCE_RESULT_OPTIONS.some((option) => option.value === value)) return value;
  return LEGACY_SOURCE_RESULT_MAP[value] || value;
}

export function normalizePublishingAction(value) {
  if (!value) return '';
  if (PUBLISHING_ACTION_OPTIONS.some((option) => option.value === value)) return value;
  return LEGACY_ACTION_MAP[value] || value;
}

export function normalizeDisplayState(value) {
  if (!value) return '';
  if (DISPLAY_STATE_OPTIONS.some((option) => option.value === value)) return value;
  return LEGACY_DISPLAY_STATE_MAP[value] || value;
}

export function normalizeVisibilityAudience(value) {
  if (!value) return '';
  if (VISIBILITY_AUDIENCE_OPTIONS.some((option) => option.value === value)) return value;
  return LEGACY_AUDIENCE_MAP[value] || value;
}

export function getDisplayAreaDefinition(value) {
  return PUB_DISPLAY_AREA_OPTIONS.find((option) => option.value === normalizeDisplayArea(value)) || null;
}

export function getSourceResultDefinition(value) {
  return SOURCE_RESULT_OPTIONS.find((option) => option.value === normalizeSourceResult(value)) || null;
}

export function getDisplayAreasForModule(moduleValue) {
  const module = normalizePublishingModule(moduleValue);
  return PUB_DISPLAY_AREA_OPTIONS.filter((option) => option.module === module);
}

export function getSourceResultsForDisplayArea(displayArea, { includeUnavailable = false } = {}) {
  const area = normalizeDisplayArea(displayArea);
  if (!area) return [];
  const areaDef = getDisplayAreaDefinition(area);
  return SOURCE_RESULT_OPTIONS.filter((option) => {
    if (!option.displayAreas.includes(area)) return false;
    if (areaDef?.target === 'whole_profile' && option.itemOnly) return false;
    if (!includeUnavailable && option.available === false) return false;
    return true;
  });
}

export function getPublishingActionsForScope(displayArea, sourceResult) {
  const source = getSourceResultDefinition(sourceResult);
  if (!source) return [];
  const area = normalizeDisplayArea(displayArea);
  if (!area || !source.displayAreas.includes(area)) return [];
  if (getDisplayAreaDefinition(area)?.target === 'whole_profile' && source.itemOnly) return [];
  return PUBLISHING_ACTION_OPTIONS.filter((option) => source.compatibleActions.includes(option.value));
}

export function deriveDisplayState(publishingAction) {
  const action = PUBLISHING_ACTION_OPTIONS.find((option) => option.value === normalizePublishingAction(publishingAction));
  return action?.displayState || '';
}

export function deriveVisibilityAudience(displayArea, publishingAction) {
  const displayState = deriveDisplayState(publishingAction);
  if (!displayState) return '';
  if (displayState === 'hidden') return 'not_applicable';
  return getDisplayAreaDefinition(displayArea)?.audienceWhenVisible || '';
}

export function getSourceOwner(sourceResult, moduleValue) {
  const source = getSourceResultDefinition(sourceResult);
  if (!source) return '';
  if (source.ownerKey === 'source_module') return getPublishingModuleLabel(moduleValue);
  return source.owner || '';
}

export function getSourceResultReference(sourceResult) {
  return getSourceResultDefinition(sourceResult)?.reference || '';
}

export function getRequiredSourceContext(sourceResult) {
  return getSourceResultDefinition(sourceResult)?.requiredContext || '';
}

export function isSourceContextDefined(sourceResult) {
  const source = getSourceResultDefinition(sourceResult);
  if (!source) return false;
  return source.contextDefined !== false && Boolean(String(source.requiredContext || '').trim());
}

export function isSourceResultAvailable(sourceResult) {
  const source = getSourceResultDefinition(sourceResult);
  if (!source) return false;
  return source.available !== false;
}

export function getReferenceAvailabilityLabel(sourceResult) {
  if (!sourceResult) return '—';
  return isSourceResultAvailable(sourceResult) ? 'Available' : 'Unavailable';
}

export function getDisplayAreaBoundary(value) {
  return getDisplayAreaDefinition(value)?.boundary || '';
}

export function getGuaranteedContextKeys(sourceResult) {
  const source = getSourceResultDefinition(sourceResult);
  return source?.requiredContextKey ? [source.requiredContextKey] : [];
}

export function getAvailableConditionFields() {
  return [];
}

export function getConditionValueOptions(field) {
  return CONDITION_VALUE_OPTIONS[field] || [];
}

export function isScopeRepeatConditionField(field) {
  return SCOPE_REPEAT_FIELDS.has(field);
}

export function isGuaranteedContextConditionField() {
  return false;
}

export function buildPublishingScopeKey(rule) {
  return [
    normalizePublishingModule(rule.module) || '',
    normalizeDisplayArea(rule.displayArea || rule.surface) || '',
    normalizeSourceResult(rule.sourceResult || rule.triggerSource) || '',
  ].join('|');
}

export function findDuplicatePublishingRule(rules, candidate, excludeId) {
  const key = buildPublishingScopeKey(candidate);
  if (!candidate.module || !(candidate.displayArea || candidate.surface) || !(candidate.sourceResult || candidate.triggerSource)) {
    return null;
  }
  return (rules || []).find((rule) => rule.id !== excludeId && buildPublishingScopeKey(rule) === key) || null;
}

export function nextPublishingRuleId(rules) {
  const nums = (rules || []).map((rule) => {
    const match = String(rule.id || '').match(/^PUB-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `PUB-${String(max + 1).padStart(4, '0')}`;
}

export function createEmptyCondition() {
  return { id: `cond-${Date.now()}`, field: '', operator: '', values: [] };
}

export function createEmptyPublishingForm() {
  return {
    id: '',
    ruleName: '',
    description: '',
    module: '',
    displayArea: '',
    sourceResult: '',
    publishingAction: '',
    displayState: '',
    visibilityAudience: '',
    conditions: [],
    changeReason: '',
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function normalizeCondition(raw) {
  const values = Array.isArray(raw?.values)
    ? raw.values.filter(Boolean)
    : raw?.value
      ? [raw.value]
      : [];
  return {
    id: raw?.id || `cond-${Math.random().toString(36).slice(2, 8)}`,
    field: raw?.field || '',
    operator: raw?.operator || '',
    values,
  };
}

export function applyDerivedPublishingFields(form) {
  const displayArea = normalizeDisplayArea(form.displayArea);
  const publishingAction = normalizePublishingAction(form.publishingAction);
  return {
    ...form,
    displayArea,
    publishingAction,
    displayState: deriveDisplayState(publishingAction),
    visibilityAudience: deriveVisibilityAudience(displayArea, publishingAction),
  };
}

export function applyPublishingSelectionChange(form, field, value) {
  let next = { ...form, [field]: value };
  if (field === 'module') {
    const areas = getDisplayAreasForModule(next.module).map((option) => option.value);
    if (next.displayArea && !areas.includes(next.displayArea)) {
      /* keep incompatible for correction */
    }
  }
  if (field === 'module' || field === 'displayArea') {
    const sources = getSourceResultsForDisplayArea(next.displayArea).map((option) => option.value);
    if (next.sourceResult && !sources.includes(next.sourceResult)) {
      /* keep incompatible for correction */
    }
  }
  if (field === 'module' || field === 'displayArea' || field === 'sourceResult' || field === 'publishingAction') {
    const actions = getPublishingActionsForScope(next.displayArea, next.sourceResult).map((option) => option.value);
    if (next.publishingAction && !actions.includes(next.publishingAction)) {
      /* keep incompatible for correction */
    }
    next = applyDerivedPublishingFields(next);
  }
  return next;
}

export function normalizePublishingForm(raw) {
  if (!raw) return createEmptyPublishingForm();
  const module = normalizePublishingModule(raw.module);
  const displayArea = normalizeDisplayArea(raw.displayArea || raw.surface);
  const sourceResult = normalizeSourceResult(raw.sourceResult || raw.triggerSource || raw.triggerCondition);
  let publishingAction = normalizePublishingAction(raw.publishingAction);
  if (!publishingAction) {
    publishingAction = normalizePublishingAction(raw.displayState || raw.outcome);
  }
  const derived = applyDerivedPublishingFields({
    displayArea,
    publishingAction,
  });
  return {
    ...createEmptyPublishingForm(),
    ...raw,
    module,
    displayArea,
    sourceResult,
    publishingAction: derived.publishingAction,
    displayState: derived.displayState || normalizeDisplayState(raw.displayState),
    visibilityAudience: derived.visibilityAudience || normalizeVisibilityAudience(raw.visibilityAudience),
    description: raw.description || raw.internalDescription || '',
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : [],
    changeReason: raw.changeReason || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

export function formatConditionLine(condition) {
  const field = getConditionFieldLabel(condition.field);
  const operator = getConditionOperatorLabel(condition.operator);
  const values = (condition.values || []).map((value) => getConditionValueLabel(condition.field, value));
  return [field, operator, values.join(', ')].filter(Boolean).join(' · ');
}

export function formatConditionsPreview(conditions = []) {
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map(formatConditionLine);
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatPublishingDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${suffix}`;
}

export function sortPublishingRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

export function getPublishingModuleLabel(value) {
  return labelFromOptions(PUB_MODULE_OPTIONS, normalizePublishingModule(value));
}

export function getDisplayAreaLabel(value) {
  const normalized = normalizeDisplayArea(value);
  const available = Boolean(getDisplayAreaDefinition(normalized));
  return withUnavailableLabel(labelFromOptions(PUB_DISPLAY_AREA_OPTIONS, normalized) || String(value || ''), available);
}

export function getSourceResultLabel(value) {
  const normalized = normalizeSourceResult(value);
  return withUnavailableLabel(
    labelFromOptions(SOURCE_RESULT_OPTIONS, normalized) || String(value || ''),
    isSourceResultAvailable(normalized),
  );
}

export function getPublishingActionLabel(value) {
  return labelFromOptions(PUBLISHING_ACTION_OPTIONS, normalizePublishingAction(value));
}

export function getDisplayStateLabel(value) {
  return labelFromOptions(DISPLAY_STATE_OPTIONS, normalizeDisplayState(value));
}

export function getVisibilityAudienceLabel(value) {
  return labelFromOptions(VISIBILITY_AUDIENCE_OPTIONS, normalizeVisibilityAudience(value));
}

export function getConditionFieldLabel(value) {
  return labelFromOptions(CONDITION_FIELD_OPTIONS, value) || String(value || '');
}

export function getConditionOperatorLabel(value) {
  return labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);
}

export function getConditionValueLabel(field, value) {
  return labelFromOptions(getConditionValueOptions(field), value) || String(value || '');
}

function actorFields(stamp, name) {
  return {
    addedBy: name,
    lastUpdatedBy: name,
    dateAdded: stamp,
    lastUpdated: stamp,
  };
}

/** Table 1 illustrative rows — sorted by Last Updated newest first. */
export function buildSamplePublishingRules() {
  return [
    {
      id: 'PUB-0005',
      ruleName: 'Restore Business Reply',
      description: 'Restore a Business Reply after an authorised restoration result.',
      module: 'reviews',
      displayArea: 'business_reply',
      sourceResult: 'approved_for_restoration',
      publishingAction: 'restore',
      displayState: 'visible',
      visibilityAudience: 'public',
      conditions: [],
      changeReason: 'Added restoration for approved business replies.',
      ...actorFields('2026-08-18T09:50:00+10:00', 'Priya Shah'),
    },
    {
      id: 'PUB-0004',
      ruleName: 'Hide Existing Post for Review',
      description: 'Hide the whole Post for a qualified System Flag.',
      module: 'posts',
      displayArea: 'post',
      sourceResult: 'system_flag',
      publishingAction: 'hide',
      displayState: 'hidden',
      visibilityAudience: 'not_applicable',
      conditions: [],
      changeReason: 'Set immediate hiding for Posts receiving a System Flag.',
      ...actorFields('2026-08-18T09:40:00+10:00', 'Alex Morgan'),
    },
    {
      id: 'PUB-0003',
      ruleName: 'Publish Approved Post',
      description: 'Publish an approved Post to the public.',
      module: 'posts',
      displayArea: 'post',
      sourceResult: 'approved_for_publication',
      publishingAction: 'publish',
      displayState: 'visible',
      visibilityAudience: 'public',
      conditions: [],
      changeReason: 'Added publication for approved posts.',
      ...actorFields('2026-08-18T09:30:00+10:00', 'Daniel Lee'),
    },
    {
      id: 'PUB-0002',
      ruleName: 'Restricted Contact Details',
      description: 'Display approved structured contact details to active, verified Customers.',
      module: 'business_profiles',
      displayArea: 'structured_contact_details',
      sourceResult: 'approved_for_display',
      publishingAction: 'publish',
      displayState: 'visible',
      visibilityAudience: 'active_verified_customers_only',
      conditions: [],
      changeReason: 'Set approved contact-detail display for the eligible restricted audience.',
      ...actorFields('2026-08-18T09:20:00+10:00', 'Priya Shah'),
    },
    {
      id: 'PUB-0001',
      ruleName: 'Publish Profile Item',
      description: 'Publish the approved individual profile core-content item.',
      module: 'business_profiles',
      displayArea: 'business_profile_core_content',
      sourceResult: 'approved_for_publication',
      publishingAction: 'publish',
      displayState: 'visible',
      visibilityAudience: 'public',
      conditions: [],
      changeReason: 'Set publication for approved individual Business Profile core-content items.',
      ...actorFields('2026-08-18T09:10:00+10:00', 'Alex Morgan'),
    },
  ];
}

/** @deprecated aliases */
export const PUB_SURFACES_BY_MODULE = PUB_MODULE_OPTIONS.reduce((acc, module) => {
  acc[module.value] = getDisplayAreasForModule(module.value);
  return acc;
}, {});
export const TRIGGER_SOURCE_OPTIONS = SOURCE_RESULT_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
export const TRIGGER_CONDITION_OPTIONS = SOURCE_RESULT_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
export const PUB_TRIGGER_TYPE_OPTIONS = TRIGGER_SOURCE_OPTIONS;
export const PUB_OUTCOME_OPTIONS = DISPLAY_STATE_OPTIONS;
export const RE_RELEASE_RULE_OPTIONS = [];
export const AVAILABLE_ACTION_OPTIONS = [];
export const PUB_RULE_STATUS_OPTIONS = [];

export function getSurfacesForModule(moduleValue) {
  return getDisplayAreasForModule(moduleValue);
}

export function getTriggerSourcesForSurface(surface) {
  return getSourceResultsForDisplayArea(surface);
}

export function getTriggerConditionsForSource() {
  return [];
}

export function getTriggerTypesForSurface(surface) {
  return getTriggerSourcesForSurface(surface);
}

export function getTriggerConditionsForType() {
  return [];
}

export function getVisibilityAudiencesForScope(module, surface) {
  const audience = getDisplayAreaDefinition(surface)?.audienceWhenVisible;
  return VISIBILITY_AUDIENCE_OPTIONS.filter((option) => option.value === audience || option.value === 'not_applicable');
}

export function getDisplayStatesForAudience() {
  return DISPLAY_STATE_OPTIONS;
}

export function getReReleaseRulesForScope() {
  return [];
}

export function getAvailableActionsForScope() {
  return [];
}

export function normalizePublishingSurface(value) {
  return normalizeDisplayArea(value);
}

export function normalizePublishingTriggerSource(value) {
  return normalizeSourceResult(value);
}

export function normalizePublishingDisplayState(value) {
  return normalizeDisplayState(value);
}

export function normalizePublishingOutcome(value) {
  return normalizeDisplayState(value);
}

export function normalizePublishingStatus() {
  return '';
}

export function getPublishingSurfaceLabel(value) {
  return getDisplayAreaLabel(value);
}

export function getPublishingTriggerSourceLabel(value) {
  return getSourceResultLabel(value);
}

export function getPublishingTriggerConditionLabel(value) {
  return getSourceResultLabel(value);
}

export function getReReleaseRuleLabel() {
  return '';
}

export function getAvailableActionLabel() {
  return '';
}

export function getPublishingRuleStatusLabel() {
  return '';
}

export function buildPublishingRuleKey(rule) {
  return buildPublishingScopeKey(rule);
}

export function buildPublishingConflictKey(rule) {
  return buildPublishingScopeKey(rule);
}

export function findConflictingPublishingRule() {
  return null;
}
